import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Layout as AntLayout, Dropdown, Button, Badge, Avatar, Space, ConfigProvider } from 'antd';
import { 
    UserOutlined, 
    DownOutlined, 
    LogoutOutlined, 
    MailOutlined,
    ContactsOutlined,
    SettingOutlined,
    TeamOutlined,
    BgColorsOutlined,
    DatabaseOutlined,
    ProfileOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import '../../styles/layout/Layout.css';

import { useAuth } from '../../hooks/useAuth';
import { useCustomization } from '../../contexts/CustomizationContext';
import { useBecomeGuest } from '../../contexts/BecomeGuestContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import NotificationBell from '../common/NotificationBell';

const { Header } = AntLayout;

const Layout = ({ children }) => {
    const { isLoggedIn, user, logout, isAdmin, updateUser } = useAuth();
    const { customization } = useCustomization();
    const { isFreeAccount } = useBecomeGuest();
    const [freeAccountStatus, setFreeAccountStatus] = useState(null);
    const [hasMailboxAccess, setHasMailboxAccess] = useState(false);
    const [emailIsFreeAccount, setEmailIsFreeAccount] = useState(false);
    const { t } = useTranslation();
    const location = useLocation();
    const isMailboxPage = location.pathname === '/mailbox';
    const topRef = useRef(null);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

    const handleLogoClick = (e) => {
        const mailboxPath = isLoggedIn ? '/profile/mailbox' : (showFreeAccountNav ? '/mailbox' : null);
        if (mailboxPath && location.pathname === mailboxPath) {
            e.preventDefault();
            topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleProfileDropdownVisibleChange = (visible) => {
        setProfileDropdownOpen(visible);
    };

    const handleAdminDropdownVisibleChange = (visible) => {
        setAdminDropdownOpen(visible);
    };

    // Function to refresh user profile data from the server
    const refreshUserProfile = async () => {
        if (!isLoggedIn) return;
        
        try {
            const response = await fetch('/api/profile/', {
                method: 'GET',
                credentials: 'include',
            });
            
            if (response.ok) {
                const data = await response.json();
                // Update the user context with fresh data from the database
                updateUser(data.profile);
            }
        } catch (error) {
            console.error('Error refreshing user profile:', error);
        }
    };

    // Refresh user profile data when component mounts
    useEffect(() => {
        refreshUserProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    useEffect(() => {
        let cancelled = false;
        const doFetch = () => {
            if (isLoggedIn || cancelled) return;
            fetch('/api/contacts-mailbox/status/', { credentials: 'include' })
                .then((res) => res.json())
                .then((data) => {
                    if (!cancelled) {
                        setFreeAccountStatus(data.is_free_account === true);
                        setHasMailboxAccess(data.has_mailbox_access === true);
                        setEmailIsFreeAccount(data.email_is_free_account === true);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setFreeAccountStatus(false);
                        setHasMailboxAccess(false);
                        setEmailIsFreeAccount(false);
                    }
                });
        };
        doFetch();
        const onAccessGranted = () => doFetch();
        window.addEventListener('contacts-mailbox-access-granted', onAccessGranted);
        return () => {
            cancelled = true;
            window.removeEventListener('contacts-mailbox-access-granted', onAccessGranted);
        };
    }, [isLoggedIn]);

    const showFreeAccountNav = isFreeAccount || freeAccountStatus === true;
    const showBecomeGuest = hasMailboxAccess && !emailIsFreeAccount;

    const logoHref = isLoggedIn ? '/profile/mailbox' : (showFreeAccountNav ? '/mailbox' : '/');

    const handleFreeAccountLogout = async () => {
        try {
            await fetch('/api/contacts-mailbox/logout/', { method: 'POST', credentials: 'include' });
        } catch (e) {
            // ignore
        }
        setFreeAccountStatus(false);
        window.location.href = '/';
    };

    const profileMenu = (
        <Menu className="custom-dropdown-menu">
            <Menu.ItemGroup title={
                <div className="dropdown-user-info">
                    <Avatar 
                        size={32} 
                        icon={<UserOutlined />} 
                        className="dropdown-avatar"
                        src={user?.avatar}
                    />
                    <div className="dropdown-user-details">
                        <div className="dropdown-username">{user?.firstname} {user?.lastname}</div>
                        <div className="dropdown-email">{user?.email}</div>
                    </div>
                </div>
            }>
                <Menu.Item key="mailbox" icon={<MailOutlined />} className="dropdown-item">
                    <Link to="/profile/mailbox">
                        <div className="dropdown-item-content">
                            <span>{t('layout.navigation.mailbox')}</span>
                            <Badge count={0} size="small" className="dropdown-badge" />
                        </div>
                    </Link>
                </Menu.Item>
                <Menu.Item key="contacts" icon={<ContactsOutlined />} className="dropdown-item">
                    <Link to="/profile/contacts">{t('layout.navigation.contacts')}</Link>
                </Menu.Item>
            </Menu.ItemGroup>
            
            <Menu.Divider />
            
            <Menu.ItemGroup>
                <Menu.Item key="profile" icon={<ProfileOutlined />} className="dropdown-item">
                    <Link to="/profile">{t('layout.navigation.profile')}</Link>
                </Menu.Item>
                <Menu.Item key="user-settings" icon={<SettingOutlined />} className="dropdown-item">
                    <Link to="/profile/user-settings">{t('layout.navigation.userSettings')}</Link>
                </Menu.Item>
            </Menu.ItemGroup>
            
            <Menu.Divider />
            
            <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout} className="dropdown-item logout-item">
                {t('layout.navigation.signOut')}
            </Menu.Item>
        </Menu>
    );

    const adminMenu = (
        <Menu className="custom-dropdown-menu admin-dropdown">
            <Menu.ItemGroup>
                <Menu.Item key="user-management" icon={<TeamOutlined />} className="dropdown-item">
                    <Link to="/admin/user-management">{t('layout.navigation.userManagement')}</Link>
                </Menu.Item>
                <Menu.Item key="customization" icon={<BgColorsOutlined />} className="dropdown-item">
                    <Link to="/admin/customization">{t('layout.navigation.customization')}</Link>
                </Menu.Item>
                <Menu.Item key="settings" icon={<DatabaseOutlined />} className="dropdown-item">
                    <Link to="/admin/system-settings">{t('layout.navigation.systemSettings')}</Link>
                </Menu.Item>
            </Menu.ItemGroup>
        </Menu>
    );

    return (
        <AntLayout className="layout-container">
            <div ref={topRef} />
            <Header className="header">
                <Link 
                    to={logoHref}
                    className="logo-container" 
                    onClick={handleLogoClick}
                >
                    {customization.logo_url ? (
                        <img src={customization.logo_url} alt={customization.company_name} className="logo" />
                    ) : (
                        <span className="logo-text">{customization.company_name}</span>
                    )}
                </Link>
                
                <nav className="nav">
                    <LanguageSwitcher />
                    {isLoggedIn ? (
                        <ConfigProvider wave={{ disabled: true }}>
                            <Space size="medium" className="nav-authenticated">
                            <NotificationBell />
                            {isAdmin && (
                                <Dropdown 
                                    overlay={adminMenu} 
                                    placement="bottomRight"
                                    trigger={['click']}
                                    open={adminDropdownOpen}
                                    onOpenChange={handleAdminDropdownVisibleChange}
                                    overlayClassName="professional-dropdown"
                                    offset={[0, 0]}
                                >
                                    <Button 
                                        type="text" 
                                        className={`nav-dropdown admin-dropdown-trigger ${adminDropdownOpen ? 'active' : ''}`}
                                    >
                                        <SettingOutlined className="dropdown-trigger-icon" />
                                        {t('layout.navigation.administration')} 
                                        <DownOutlined className={`dropdown-arrow ${adminDropdownOpen ? 'rotated' : ''}`} />
                                    </Button>
                                </Dropdown>
                            )}
                            
                            <Dropdown 
                                overlay={profileMenu} 
                                placement="bottomRight"
                                trigger={['click']}
                                open={profileDropdownOpen}
                                onOpenChange={handleProfileDropdownVisibleChange}
                                overlayClassName="professional-dropdown"
                                offset={[0, 0]}
                            >
                                <Button 
                                    type="text" 
                                    className={`nav-dropdown profile-dropdown-trigger ${profileDropdownOpen ? 'active' : ''}`}
                                >
                                    <Avatar 
                                        size={24} 
                                        icon={<UserOutlined />} 
                                        src={user?.avatar}
                                        className="nav-avatar"
                                    />
                                    {user?.firstname || t('layout.navigation.profile')} 
                                    <DownOutlined className={`dropdown-arrow ${profileDropdownOpen ? 'rotated' : ''}`} />
                                </Button>
                            </Dropdown>
                        </Space>
                        </ConfigProvider>
                    ) : showFreeAccountNav ? (
                        <div className="nav-auth-buttons">
                            <Link
                                to="/mailbox"
                                className="nav-auth-btn nav-auth-btn-login"
                                aria-label={t('layout.navigation.mailboxAriaLabel', { defaultValue: 'Access your mailbox' })}
                            >
                                {t('layout.navigation.mailbox')}
                            </Link>
                            <Link
                                to="#"
                                className="nav-auth-btn nav-auth-btn-logout"
                                onClick={(e) => { e.preventDefault(); handleFreeAccountLogout(); }}
                                aria-label={t('contactsMailbox.logout', { defaultValue: 'Log out' })}
                            >
                                <LogoutOutlined style={{ marginRight: 6 }} />
                                {t('contactsMailbox.logout') || 'Log out'}
                            </Link>
                        </div>
                    ) : showBecomeGuest ? (
                        <div className="nav-auth-buttons">
                            <Link
                                to="/become-guest"
                                className="nav-auth-btn nav-auth-btn-guest"
                                aria-label={t('contactsMailbox.becomeGuest', { defaultValue: 'Become a Guest' })}
                            >
                                {t('contactsMailbox.becomeGuest', 'Become a Guest')}
                            </Link>
                            <Link
                                to="#"
                                className="nav-auth-btn nav-auth-btn-logout"
                                onClick={(e) => { e.preventDefault(); handleFreeAccountLogout(); }}
                                aria-label={t('contactsMailbox.logout', { defaultValue: 'Log out' })}
                            >
                                <LogoutOutlined style={{ marginRight: 6 }} />
                                {t('contactsMailbox.logout') || 'Log out'}
                            </Link>
                        </div>
                    ) : (
                        <div className="nav-auth-buttons">
                            <Link
                                to="/login"
                                className="nav-auth-btn nav-auth-btn-login"
                                aria-label={t('layout.navigation.loginAriaLabel', { defaultValue: 'Sign in to your account' })}
                            >
                                {t('layout.navigation.login')}
                            </Link>
                            <Link
                                to="/register"
                                className="nav-auth-btn nav-auth-btn-signup"
                                aria-label={t('layout.navigation.signUpAriaLabel', { defaultValue: 'Create a new account' })}
                            >
                                {t('layout.navigation.signUp')}
                            </Link>
                        </div>
                    )}
                </nav>
            </Header>
            <main className="main-content">
                {children}
            </main>
        </AntLayout>
    );
};

export default Layout;